import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";
import { useQuery } from "urql";

import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { graphql } from "@/gen/gql";
import useDebounce from "@/hooks/useDebounce";
import { useSearchTournaments } from "@/hooks/useSearchTournaments";
import { cn, cx } from "@/utils/cx";

const tournamentQuery = graphql(`
    query TournamentQueryById($id: UUID!) {
        tournament(id: $id) {
            id
            title
        }
    }
`);

function useTournamentId(tournamentId: string | null) {
    const [result] = useQuery({
        query: tournamentQuery,
        variables: {
            id: tournamentId!,
        },
        pause: !tournamentId,
    });
    return result.data?.tournament;
}

export const TournamentIdInput = ({
    tournamentId,
    onTournamentIdChange,
    className,
}: {
    tournamentId: string | null;
    onTournamentIdChange: (playerId: string | null) => void;
    className?: string;
}) => {
    const [open, setOpen] = useState(false);

    const selectedTournament = useTournamentId(tournamentId);

    const [query, setQuery] = useState("");
    const debouncedQuery = useDebounce(query, 500);

    const { tournaments } = useSearchTournaments({
        query: debouncedQuery,
        pause: !debouncedQuery,
    });

    return (
        <div className={cx("flex flex-col gap-2", className)}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="justify-between"
                    >
                        {selectedTournament ? selectedTournament.title : "Find tournament..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0">
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder="Search for tournament..."
                            value={query}
                            onInput={e => setQuery(e.currentTarget.value)}
                        />
                        <CommandList>
                            <CommandEmpty>No matching tournaments found.</CommandEmpty>
                            <CommandGroup>
                                {tournaments.map(tournament => (
                                    <CommandItem
                                        key={tournament.id}
                                        value={tournament.id}
                                        onSelect={currentValue => {
                                            onTournamentIdChange(
                                                selectedTournament?.id === currentValue
                                                    ? null
                                                    : currentValue
                                            );
                                            setOpen(false);
                                        }}
                                    >
                                        <Check
                                            className={cn(
                                                "mr-2 h-4 w-4",
                                                selectedTournament?.id === tournament.id
                                                    ? "opacity-100"
                                                    : "opacity-0"
                                            )}
                                        />
                                        {tournament.title}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    );
};
